// frontend/src/pages/yard/YardReeferLog.tsx
import { isAxiosError } from 'axios';
import { formatGenset } from '../../utils/reeferGenset';
import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { PlusIcon, BoltIcon } from '@heroicons/react/24/outline';
import yardService from '../../services/yard.service';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import SingleSelect from '../../components/SingleSelect';
import {
  TempScale,
  ReeferLogRequest,
  ReeferInYard,
  YardLocation,
} from '../../types/yard';
import { useToast } from '../../contexts/ToastContext';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

type ContextType = {
  selectedYard: number | null;
  setSelectedYard: (yard: number | null) => void;
};

export default function YardReeferLog() {
  const { showToast } = useToast();

  // Guard-layout context (yardlocations.recno). May be absent outside GuardLayout.
  const context = useOutletContext<ContextType | null>();
  const guardYardId = context?.selectedYard ?? null;

  // Yardlocations list (matches the Yard Inventory dropdown)
  const [yards, setYards] = useState<YardLocation[]>([]);
  const [selectedYardId, setSelectedYardId] = useState<number | null>(null);
  const selectedYard = yards.find((y) => y.recno === selectedYardId) ?? null;

  // Reefer list
  const [reefers, setReefers] = useState<ReeferInYard[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Set Temp modal state
  const [showTempModal, setShowTempModal] = useState(false);
  const [modalReefer, setModalReefer] = useState<ReeferInYard | null>(null);
  const [modalManualEquipno, setModalManualEquipno] = useState('');
  const [modalManualChassisno, setModalManualChassisno] = useState('');
  const [temperature, setTemperature] = useState('');
  const [tempScale, setTempScale] = useState<TempScale>('F');
  const [submittedGenset, setSubmittedGenset] = useState<'Y' | 'N' | ''>('');
  // Genset Unit # (ADO 32528): required when submittedGenset = 'Y', hidden otherwise.
  const [gensetUnit, setGensetUnit] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Header-drag for the Set Temperature modal. Declared at the component top
  // level (not inside renderTempModal) so hook order stays stable.
  const tempDrag = useModalDrag(showTempModal);

  // Order Details Drawer state — opened when a PRO is clicked.
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Load yardlocations (same source as the Yard Inventory dropdown)
  useEffect(() => {
    yardService.getYardLocations().then(setYards).catch(console.error);
  }, []);

  // Auto-select the guard's default yard once yardlocations have loaded.
  useEffect(() => {
    if (guardYardId == null || yards.length === 0) return;
    if (selectedYardId != null) return; // don't override a user-made selection
    if (yards.some((y) => y.recno === guardYardId)) {
      setSelectedYardId(guardYardId);
    }
  }, [guardYardId, yards, selectedYardId]);

  // Load reefers when yard changes
  const loadReefers = useCallback(async () => {
    if (!selectedYard) {
      setReefers([]);
      return;
    }
    setLoading(true);
    try {
      const data = await yardService.getReefersInYard(selectedYard.recno);
      setReefers(data);
    } catch (error) {
      console.error('Error loading reefers:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYard]);

  useEffect(() => {
    loadReefers();
  }, [loadReefers]);

  // --- Filtering & splitting ---

  const isLoggedToday = (dateStr: string | null) => {
    if (!dateStr) return false;
    const logged = new Date(dateStr);
    const now = new Date();
    return (
      logged.getFullYear() === now.getFullYear() &&
      logged.getMonth() === now.getMonth() &&
      logged.getDate() === now.getDate()
    );
  };

  const filteredReefers = reefers.filter((item) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      item.equipno.toLowerCase().includes(term) ||
      (item.chassisno?.toLowerCase() || '').includes(term) ||
      item.companyname.toLowerCase().includes(term) ||
      (item.customer_name?.toLowerCase() || '').includes(term) ||
      String(item.cmspronumber).includes(term)
    );
  });

  const pendingItems = filteredReefers.filter((r) => !isLoggedToday(r.last_logged));
  const recordedItems = filteredReefers.filter((r) => isLoggedToday(r.last_logged));

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatTemp = (temp: string | null, scale: string | null) => {
    if (!temp) return '-';
    return `${temp}°${scale || ''}`.trim();
  };

  // --- Set Temp modal ---

  const openTempModal = (reefer: ReeferInYard | null) => {
    setModalReefer(reefer);
    setModalManualEquipno('');
    setModalManualChassisno('');
    setTemperature('');
    setSubmittedGenset('');
    // Prefill from the last log so a repeat reading on the same genset is one tap.
    setGensetUnit((reefer?.last_gensetunit || '').toUpperCase());
    setComments('');
    setTempScale((reefer?.required_tempscale as TempScale) || 'F');
    setShowTempModal(true);
  };

  const closeTempModal = () => {
    setShowTempModal(false);
    setModalReefer(null);
    setModalManualEquipno('');
    setModalManualChassisno('');
    setTemperature('');
    setSubmittedGenset('');
    setGensetUnit('');
    setComments('');
  };

  // On-screen keypad for the temperature field. The mobile decimal keyboard has
  // no minus key, so all temp entry goes through these buttons (desktop too).
  // Keeps `temperature` matching /^-?\d*\.?\d*$/ at all times.
  const pressTempKey = (key: string) => {
    setTemperature((prev) => {
      switch (key) {
        case 'clear':
          return '';
        case 'back':
          return prev.slice(0, -1);
        case '-':
          // Toggle the sign of the whole value.
          return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
        case '.': {
          if (prev.includes('.')) return prev; // only one decimal point
          const base = prev === '' || prev === '-' ? prev + '0' : prev;
          return base + '.';
        }
        default: {
          // Digit. Replace a lone leading zero ("0" -> "5", "-0" -> "-5") and
          // cap total digits so a stuck finger can't run away.
          if (prev === '0') return key;
          if (prev === '-0') return '-' + key;
          if (prev.replace(/[-.]/g, '').length >= 6) return prev;
          return prev + key;
        }
      }
    });
  };

  const handleTempSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedYard) {
      showToast({ message: 'Please select a yard', type: 'error' });
      return;
    }
    const equipno = modalReefer?.equipno || modalManualEquipno;
    if (!equipno) {
      showToast({ message: 'Please enter equipment number', type: 'error' });
      return;
    }
    if (!/\d/.test(temperature)) {
      showToast({ message: 'Please enter temperature', type: 'error' });
      return;
    }

    if (submittedGenset !== 'Y' && submittedGenset !== 'N') {
      showToast({ message: 'Please select Genset Yes or No', type: 'error' });
      return;
    }
    const gensetunit = submittedGenset === 'Y' ? gensetUnit.trim() : '';
    if (submittedGenset === 'Y' && !gensetunit) {
      showToast({ message: 'Please enter the Genset Unit #', type: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const request: ReeferLogRequest = {
        yardrecno: selectedYard.recno,
        equipno,
        cmspronumber: modalReefer?.cmspronumber || 0,
        temperature,
        tempscale: tempScale,
        pluggedin: submittedGenset,
        gensetunit,
        manualentry: modalReefer ? 'N' : 'Y',
        chassisno: modalReefer ? (modalReefer.chassisno || '') : modalManualChassisno,
        comments: comments.trim(),
      };
      const result = await yardService.logReeferTemp(request);
      showToast({ message: result.message || 'Temperature logged', type: 'success' });
      closeTempModal();
      loadReefers();
    } catch (error: unknown) {
      showToast({
        message: (isAxiosError<{ detail?: string }>(error) && error.response?.data?.detail) || 'Error logging temperature',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render helpers ---

  const renderRow = (item: ReeferInYard, isPending: boolean) => (
    <div
      key={item.recno}
      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
    >
      {/* Info - wraps on mobile, single row on desktop */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 flex-1 min-w-0">
        {/* Equipment number */}
        <span className="font-semibold text-heading truncate">
          {item.equipno || '-'}
        </span>

        {/* Chassis */}
        <span className="text-muted truncate">
          {item.chassisno || '-'}
        </span>

        {/* Company / Customer */}
        <span className="text-gray-600 dark:text-gray-300 truncate sm:flex-1">
          {item.customer_name || item.companyname || 'Unknown'}
        </span>

        {/* PRO */}
        {item.cmspronumber > 0 ? (
          <button
            type="button"
            onClick={() => openOrderDrawer(item.cmspronumber)}
            className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium whitespace-nowrap"
          >
            PRO: {item.cmspronumber}
          </button>
        ) : (
          <span className="text-muted text-xs whitespace-nowrap">-</span>
        )}

        {/* Required Temp */}
        <span className="text-muted text-xs whitespace-nowrap">
          Req: {formatTemp(item.required_temp, item.required_tempscale)}
        </span>

        {/* Vent Setting */}
        {item.ventsetting && (
          <span className="text-muted text-xs whitespace-nowrap">
            Vent: {item.ventsetting}
          </span>
        )}

        <span className="text-muted text-xs whitespace-nowrap">
          Genset required: {formatGenset(item.genset, 'Not specified')}
        </span>
        <span className="text-xs whitespace-nowrap">
          Genset submitted: {formatGenset(item.pluggedin, 'Not recorded')}
          {item.last_gensetunit ? ` · Unit ${item.last_gensetunit}` : ''}
        </span>

        {/* Current (last logged) Temp */}
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
            isPending
              ? 'bg-gray-100 text-muted dark:bg-gray-700'
              : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
          }`}
        >
          {formatTemp(item.last_temp, item.last_tempscale)}
        </span>

        {/* Last logged datetime (inline on mobile after info) */}
        {!isPending && (
          <span className="text-xs text-green-600 dark:text-green-400 whitespace-nowrap">
            Checked: {formatDateTime(item.last_logged)}
          </span>
        )}
      </div>

      {/* Actions - full width on mobile, auto on desktop */}
      <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
        {isPending ? (
          <button
            onClick={() => openTempModal(item)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            <BoltIcon className="w-3.5 h-3.5" />
            Set Temp
          </button>
        ) : (
          <button
            onClick={() => openTempModal(item)}
            className="w-full sm:w-auto px-3 py-2 sm:py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Log another reading"
          >
            Re-log
          </button>
        )}
      </div>
    </div>
  );

  const renderSection = (title: string, items: ReeferInYard[], isPending: boolean) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-t border border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h2>
        <span className="text-xs text-muted bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
          {items.length}
        </span>
      </div>
      <div className="border border-t-0 border-gray-200 dark:border-gray-700 rounded-b">
        {items.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500 italic">
            {isPending ? 'All reefers checked today' : 'No temperatures recorded today yet'}
          </div>
        ) : (
          items.map((item) => renderRow(item, isPending))
        )}
      </div>
    </div>
  );

  // --- Set Temp modal ---

  const renderTempModal = () => {
    if (!showTempModal) return null;

    const displayEquipno = modalReefer?.equipno || modalManualEquipno || 'Manual Entry';

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50"
        onClick={closeTempModal}
      >
        <div
          ref={tempDrag.panelRef}
          style={tempDrag.panelStyle}
          className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader
            title={`Set Temperature — ${displayEquipno}`}
            onClose={closeTempModal}
            dragHandleProps={tempDrag.dragHandleProps}
          />

          <form onSubmit={handleTempSubmit} className="px-5 py-4 space-y-4">
            {/* Context strip */}
            {modalReefer && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-xs space-y-0.5">
                <div>
                  <strong>Customer:</strong>{' '}
                  {modalReefer.customer_name || modalReefer.companyname || 'Unknown'}
                </div>
                {modalReefer.cmspronumber > 0 && (
                  <div>
                    <strong>PRO:</strong> {modalReefer.cmspronumber}
                  </div>
                )}
                <div>
                  <strong>Required:</strong>{' '}
                  {formatTemp(modalReefer.required_temp, modalReefer.required_tempscale)}
                </div>
                {modalReefer.ventsetting && (
                  <div>
                    <strong>Vent Setting:</strong> {modalReefer.ventsetting}
                  </div>
                )}
                <div>
                  <strong>Genset required:</strong> {formatGenset(modalReefer.genset, 'Not specified')}
                </div>
              </div>
            )}

            {/* Manual equipno + chassis inputs when opening without a selected reefer */}
            {!modalReefer && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Equipment #</label>
                  <input
                    type="text"
                    value={modalManualEquipno}
                    onChange={(e) => setModalManualEquipno(e.target.value.toUpperCase())}
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                    placeholder="e.g., MSCU1234567"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Chassis # <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={modalManualChassisno}
                    onChange={(e) => setModalManualChassisno(e.target.value.toUpperCase())}
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                    placeholder="e.g., GWIZ1234"
                  />
                </div>
              </>
            )}

            {/* Temperature */}
            <div>
              <label className="block text-sm font-medium mb-1">Temperature</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="none"
                  readOnly
                  value={temperature}
                  className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-3 text-2xl text-center font-bold cursor-default select-none"
                  placeholder="32.5"
                />
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => setTempScale('F')}
                    className={`px-4 py-1 rounded font-bold text-sm ${
                      tempScale === 'F'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    °F
                  </button>
                  <button
                    type="button"
                    onClick={() => setTempScale('C')}
                    className={`px-4 py-1 rounded font-bold text-sm ${
                      tempScale === 'C'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    °C
                  </button>
                </div>
              </div>

              {/* On-screen keypad — the mobile decimal keyboard has no minus key */}
              <div className="mt-3 flex gap-2">
                <div className="grid grid-cols-3 gap-2 flex-1">
                  {['7', '8', '9', '4', '5', '6', '1', '2', '3', '-', '0', '.'].map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => pressTempKey(k)}
                      aria-label={k === '-' ? 'Toggle negative' : k}
                      className="py-3 text-2xl font-bold rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500"
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-2 w-20">
                  <button
                    type="button"
                    onClick={() => pressTempKey('clear')}
                    aria-label="Clear"
                    className="py-3 text-lg font-bold rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500"
                  >
                    C
                  </button>
                  <button
                    type="button"
                    onClick={() => pressTempKey('back')}
                    aria-label="Backspace"
                    className="flex-1 flex items-center justify-center text-2xl font-bold rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500"
                  >
                    ⌫
                  </button>
                </div>
              </div>
            </div>

            <fieldset>
              <legend className="block text-sm font-medium mb-2">Genset? (required)</legend>
              <div className="grid grid-cols-2 gap-3">
                {(['Y', 'N'] as const).map((value) => (
                  <label key={value} className={`flex items-center justify-center gap-2 py-2 text-sm font-bold rounded border cursor-pointer ${
                    submittedGenset === value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-panel text-gray-700 border-gray-300 dark:text-gray-200 dark:border-gray-600'
                  }`}>
                    <input
                      type="radio"
                      name="pluggedin"
                      value={value}
                      checked={submittedGenset === value}
                      onChange={() => setSubmittedGenset(value)}
                      required
                    />
                    {value === 'Y' ? 'Yes (Y)' : 'No (N)'}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Genset Unit # — only when a genset was submitted; required. */}
            {submittedGenset === 'Y' && (
              <div>
                <label htmlFor="reefer-log-genset-unit" className="block text-sm font-medium mb-1">
                  Genset Unit # <span className="text-gray-400 font-normal">(required)</span>
                </label>
                <input
                  id="reefer-log-genset-unit"
                  type="text"
                  value={gensetUnit}
                  onChange={(e) => setGensetUnit(e.target.value.toUpperCase().slice(0, 25))}
                  maxLength={25}
                  autoFocus
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                  placeholder="e.g., GS1234"
                />
              </div>
            )}

            <div>
              <label htmlFor="reefer-log-comment" className="block text-sm font-medium mb-1">
                Comment <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="reefer-log-comment"
                value={comments}
                onChange={(e) => setComments(e.target.value.slice(0, 255))}
                rows={2}
                maxLength={255}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                placeholder="Anything the LC should know (unit cycling, door open, etc.)"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-2 border-t dark:border-gray-700">
              <button
                type="button"
                onClick={closeTempModal}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  submitting || !/\d/.test(temperature) || submittedGenset === '' ||
                  (submittedGenset === 'Y' && !gensetUnit.trim())
                }
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting ? 'Logging...' : 'Log Temperature'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // --- Main render ---

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold dark:text-white">Reefer Temperature Log</h1>
        <div className="flex items-center gap-2">
          {selectedYard && (
            <>
              <button
                onClick={() => openTempModal(null)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Manual Log
              </button>
              <button
                onClick={loadReefers}
                className="text-blue-600 hover:text-blue-800 text-sm px-2 py-1.5"
              >
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Yard selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 dark:text-white">Select Yard</label>
        <SingleSelect
          options={yards.map((yard) => ({ value: String(yard.recno), label: yard.locname }))}
          value={selectedYardId != null ? String(selectedYardId) : ''}
          onChange={(v) => setSelectedYardId(v ? Number(v) : null)}
          placeholder="-- Select Yard --"
          enableSearch
          searchPlaceholder="Search yards…"
          className="w-full"
        />
      </div>

      {selectedYard && (
        <>
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search equipment, chassis, company, PRO..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Summary */}
          <div className="mb-4 flex gap-4 text-sm">
            <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200 px-3 py-2 rounded">
              <span className="font-medium">{filteredReefers.length}</span> reefers
              {searchTerm && ` (of ${reefers.length})`}
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-300 px-3 py-2 rounded">
              <span className="font-medium">{recordedItems.length}</span> recorded today
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-300 px-3 py-2 rounded">
              <span className="font-medium">{pendingItems.length}</span> pending
            </div>
          </div>

          {/* Sections */}
          {loading ? (
            <div className="text-center py-8 text-muted">Loading...</div>
          ) : (
            <>
              {renderSection('Pending Temp Check', pendingItems, true)}
              {renderSection('Temp Recorded', recordedItems, false)}
            </>
          )}
        </>
      )}

      {renderTempModal()}

      {/* Order Details Drawer (opened from PRO link) */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}
